import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { type LucideIcon, Loader2 } from "lucide-react";
import Link from "next/link";

import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-light",
        peach:
          "bg-accent-peach text-foreground hover:bg-primary-light hover:text-primary-foreground",
        black:
          "bg-foreground text-white hover:bg-primary-light hover:text-primary-foreground",
        // blg: "bg-primary-light text-foreground border border-primary-dark hover:bg-transparent hover:text-foreground",
        accent:
          "bg-accent text-accent-foreground border hover:border-primary hover:bg-transparent hover:text-foreground",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-secondary text-muted-light bg-background hover:bg-accent hover:text-accent-foreground hover:text-white hover:bg-secondary hover:border-secondary px-4 font-bold ",
        secondary:
          "bg-secondary text-secondary-foreground border border-secondary hover:bg-transparent hover:text-foreground [&_svg]:hover:fill-secondary px-4 font-bold [&_span]:w-3/5 [&_span]:text-center",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        icon: "hover:scale-105 opacity-60 hover:opacity-100",
        link: "text-primary underline-offset-4 hover:underline h-0 justify-start",
        unstlyed: "",
      },
      size: {
        default: "h-9 px-4 py-2 [&_svg]:size-4 md:[&_svg]:size-6",
        sm: "h-8 rounded-md px-3 text-xs [&_svg]:size-4",
        lg: "h-10 sm:h-12 rounded-md px-4 min-w-48 [&_svg]:size-4 md:[&_svg]:size-6",
        xl: "h-10 sm:h-14 min-w-48 font-bold text-base [&_svg]:size-4 md:[&_svg]:size-6",
        icon: "h-4 w-4 p-0",
      },
      // iconSize: {
      //   sm: "[&_svg]:size-4",
      //   default: "[&_svg]:size-4 md:[&_svg]:size-6",
      //   lg: "[&_svg]:size-5 md:[&_svg]:size-7",
      // },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      // iconSize: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: LucideIcon;
  loading?: boolean;
  fullWidth?: boolean;
  blacktxt?: boolean;
  link?: string;
  target?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      blacktxt,
      variant,
      size,
      // iconSize,
      asChild = false,
      link,
      loading,
      fullWidth,
      children,
      disabled,
      target,
      type = "button",
      ...props
    },
    ref,
  ) => {
    // Determine the component to render
    const Comp: React.ElementType = asChild ? Slot : "button";

    if (link) {
      return (
        <Link
          href={link}
          target={target}
          className={cn(
            buttonVariants({ variant, size }),
            fullWidth && "w-full",
            className,
            blacktxt && "text-foreground",
          )}
        >
          {children}
        </Link>
      );
    }

    // if no size and variant is icon, set size to icon
    if (!size && variant === "icon") size = "icon";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          fullWidth && "w-full",
          className,
          blacktxt && "text-foreground",
        )}
        ref={ref}
        disabled={disabled || loading}
        type={type}
        {...props}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
